import { effect, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IncidentRulesService as ApiIncidentRulesService } from '@api/devices';
import { type DeviceData, type DeviceIncidentRule, DevicesActions, type IncidentRuleBase, type IncidentRuleCommon } from '@devices';
import { Actions, ofType } from '@ngrx/effects';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { addEntities, addEntity, removeAllEntities, removeEntity, updateEntity, withEntities } from '@ngrx/signals/entities';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';

interface IncidentRulesState {
  device: DeviceData | null;
  isDeleting: boolean;
  isLoading: boolean;
  isUpdatingOrCreating: boolean;
}

export const IncidentRulesStore = signalStore(
  withEntities<DeviceIncidentRule>(),
  withState<IncidentRulesState>({
    device: null,
    isDeleting: false,
    isLoading: false,
    isUpdatingOrCreating: false,
  }),
  withMethods(
    (store, api = inject(ApiIncidentRulesService), ngrxStore = inject(Store)) => ({
      async createIncidentRule(body: IncidentRuleBase) {
        const device = store.device();

        if (!device) {
          return Promise.resolve(null);
        }

        patchState(store, { isUpdatingOrCreating: true });

        try {
          const response = await firstValueFrom(api.create({ deviceId: device.id }, body));
          const incidentRule = response.data as DeviceIncidentRule;

          patchState(store, addEntity(incidentRule));

          ngrxStore.dispatch(DevicesActions.createDeviceIncidentRuleSuccess({ rule: incidentRule }));

          return incidentRule;
        } catch (e) {
          throw e;
        } finally {
          patchState(store, { isUpdatingOrCreating: false });
        }
      },
      async deleteIncidentRule(incidentRule: IncidentRuleCommon) {
        const device = store.device();

        if (!device || !incidentRule) {
          return Promise.resolve(false);
        }

        patchState(store, { isDeleting: true });

        try {
          await firstValueFrom(api.delete({ deviceId: device.id, id: incidentRule.id }));
          patchState(store, removeEntity(incidentRule.id));

          ngrxStore.dispatch(DevicesActions.deleteDeviceIncidentRuleSuccess({ deviceId: device.id, incidentRuleId: incidentRule.id }));

          return true;
        } catch (e) {
          throw e;
        } finally {
          patchState(store, { isDeleting: false });
        }
      },
      async fetchIncidentRuleList() {
        const device = store.device();

        if (!device) {
          return;
        }

        patchState(store, { isLoading: true });

        const response = await firstValueFrom(api.findAll({ deviceId: device.id }));
        const incidentRules = (response.data ?? []) as DeviceIncidentRule[];

        patchState(store, addEntities(incidentRules));
        patchState(store, { isLoading: false });
      },
      setDevice(device: DeviceData) {
        if (store.device()?.id === device.id) {
          patchState(store, { device });

          return;
        }
        patchState(store, { device }, removeAllEntities());
      },
      async updateIncidentRule(incidentRuleId: string, body: Partial<DeviceIncidentRule>) {
        const device = store.device();

        if (!device) {
          return Promise.resolve(null);
        }

        patchState(store, { isUpdatingOrCreating: true });

        try {
          const response = await firstValueFrom(
            api.update({ deviceId: device.id, id: incidentRuleId }, body as Parameters<typeof api.update>[1]),
          );
          const incidentRule = response.data as DeviceIncidentRule;

          patchState(store, updateEntity({ changes: incidentRule, id: incidentRuleId }));

          ngrxStore.dispatch(DevicesActions.updateDeviceIncidentRuleSuccess({ rule: incidentRule }));

          return incidentRule;
        } catch (e) {
          throw e;
        } finally {
          patchState(store, { isUpdatingOrCreating: false });
        }
      },

    }),
  ),
  withHooks({
    onInit(store) {
      const actions$ = inject(Actions);

      actions$.pipe(
        ofType(DevicesActions.updateDeviceIncidentRuleSuccess),
        takeUntilDestroyed(),
      ).subscribe(({ rule }) => {
        if (store.device()?.id === rule.deviceId) {
          patchState(store, updateEntity({ changes: rule, id: rule.id }));
        }
      });

      actions$.pipe(
        ofType(DevicesActions.createDeviceIncidentRuleSuccess),
        takeUntilDestroyed(),
      ).subscribe(({ rule }) => {
        if (store.device()?.id === rule.deviceId) {
          patchState(store, addEntity(rule));
        }
      });

      actions$.pipe(
        ofType(DevicesActions.deleteDeviceIncidentRuleSuccess),
        takeUntilDestroyed(),
      ).subscribe(({ deviceId, incidentRuleId }) => {
        if (store.device()?.id === deviceId) {
          patchState(store, removeEntity(incidentRuleId));
        }
      });

      effect(async () => {
        if (store.device()) {
          await store.fetchIncidentRuleList();
        }
      });
    },
  }),
);
